import _ from "underscore";
import ChartGenerator from "../../../../../js/chart/ChartGenerator";
import {getMaxMinScaleValues, getVerticalGridCount} from "../../../../../js/chart/utils";

function BalancesChartGenerator() {}

BalancesChartGenerator.prototype = _.create(ChartGenerator.prototype, {
    MAX_VERTICAL_GRIDS: 7,

    _prepareChartSettings: function(chartData, config)
    {
        var collectedData = this._getCollectedData(chartData, config);

        var colorScheme = config["colorScheme"];
        var balanceFormatFunction = config["balanceFormatFunction"];
        var chartScrollbar = this._getChartScrollbar(chartData, config);

        var categoryAxisLabelFunction = config["categoryAxisLabelFunction"];

        var maxMinScaleValues = this._getMaxMinScaleValues(collectedData, config);
        var gridCount = getVerticalGridCount(maxMinScaleValues.maxScaleValue, maxMinScaleValues.minScaleValue, this.MAX_VERTICAL_GRIDS);

        var chartMargins = this._getMargins(balanceFormatFunction(maxMinScaleValues.maxScaleValue), balanceFormatFunction(maxMinScaleValues.minScaleValue));

        var graphs = this._prepareChartGraphs(colorScheme, balanceFormatFunction, config["balloonConfig"]);
        var dataProvider = collectedData.balancesData;

        return {
            "type": "serial",
            "autoResize": false,

            "startDuration": 0,
            "startEffect": "easeOutSine",
            "sequencedAnimation": true,

            "theme": "light",
            "columnWidth": 0.55,
            "fontSize": 11,
            "addClassNames": true,

            "autoMargins": false,
            "marginTop": chartMargins["top"],
            "marginBottom": chartMargins["bottom"],
            "marginLeft": chartMargins["left"],
            "marginRight": chartMargins["right"],

            "graphs": graphs,

            "valueAxes": [
                {
                    "id": "main",
                    "maximum": maxMinScaleValues.maxScaleValue,
                    "minimum": maxMinScaleValues.minScaleValue,
                    "autoGridCount": false,
                    "strictMinMax": true,
                    "gridCount": gridCount,
                    "labelFunction": balanceFormatFunction
                },
                {
                    "id": "hidden",
                    "gridCount": 0,
                    "position": "right",
                    "synchronizeWith": "main",
                    "synchronizationMultiplier": 1,
                    "autoGridCount": false,
                    "axisAlpha": 0.11,
                    // it need for show axis if auto margin off (hack)
                    "minimum": 0,
                    "logarithmic": true
                }
            ],

            "chartScrollbar": chartScrollbar,

            "categoryField": "category",
            "categoryAxis": {
                "gridPosition": "start",
                "color": "#414141",
                "categoryFunction": categoryAxisLabelFunction,
                "labelRotation": 48,
                "labelFrequency": 1,
                "minHorizontalGap": 65,
                "minVerticalGap" : 50,
                "fontSize": 11
            },
            "dataProvider": dataProvider
        }
    },

    _getMaxMinScaleValues: function(collectedData, config)
    {
        var maxMinScaleValues;
        if (_.isUndefined(config["maxMinScaleValues"]))
        {
            maxMinScaleValues = getMaxMinScaleValues(collectedData.maxBalanceValue, collectedData.minBalanceValue, this.MAX_VERTICAL_GRIDS);
            this._maxMinScaleValues = maxMinScaleValues;
        }
        else
        {
            maxMinScaleValues = config["maxMinScaleValues"]; // maxMinScaleValues from config used when chart is exporting to pdf
        }
        return maxMinScaleValues;
    },

    _getChartScrollbar: function(data, config)
    {
        if (!config["showScrollbar"] || data.length <= 22)
        {
            return null;
        }
        else
        {
            return {
                "scrollbarHeight": 16,
                "dragIconHeight": 30,
                "dragIconWidth": 30,
                "backgroundColor" : "#f9f9f9",
                "backgroundAlpha" : 1,
                "selectedBackgroundAlpha" : 1,
                "selectedBackgroundColor" : "#e8e8e8"
            }
        }
    },

    _getCollectedData: function(chartData, config)
    {
        var minBalanceValue = Number.POSITIVE_INFINITY;
        var maxBalanceValue = Number.NEGATIVE_INFINITY;
        var balanceFieldName = config["balanceFieldName"];
        for (var i = 0; i < chartData.length; i++)
        {
            var balanceData = chartData[i];

            // Nulls are casted into 0 by + (unary operator)
            minBalanceValue = Math.min(minBalanceValue, +balanceData["balanceForFirstColumn"], +balanceData["balanceForSecondColumn"]);
            maxBalanceValue = Math.max(maxBalanceValue, +balanceData["balanceForFirstColumn"], +balanceData["balanceForSecondColumn"]);
        }

        return {
            "minBalanceValue": minBalanceValue,
            "maxBalanceValue": maxBalanceValue,
            "balancesData": chartData
        };
    },

    _getMargins: function(formattedMaxScaleValue, formattedMinScaleValue)
    {
        var maxStringLength = Math.max(formattedMaxScaleValue.length, formattedMinScaleValue.length);
        var left = 72;

        if (maxStringLength > 8)
        {
            left += (maxStringLength - 8) * 8;
        }

        return {
            "top": 20,
            "bottom": 125,
            "left": left,
            "right": 20
        }
    },

    /**
     * Prepare graphs for chart. Every graph represents one type of column
     */
    _prepareChartGraphs: function(colorScheme, balanceFormatFunction, balloonConfig)
    {
        var balloonBalanceLabel = balloonConfig["balloonBalanceLabel"];
        var dateBefore = balloonConfig["dateBefore"];
        var dateAfter = balloonConfig["dateAfter"];
        var categoryFormatFunction = balloonConfig["categoryFormatFunction"];

        return [
            this._createBalanceDataGraph("balanceForFirstColumn", colorScheme["balanceForFirstColumn"], balloonBalanceLabel, balanceFormatFunction,
                                         dateBefore, categoryFormatFunction),
            this._createBalanceDataGraph("balanceForSecondColumn", colorScheme["balanceForSecondColumn"], balloonBalanceLabel, balanceFormatFunction,
                                         dateAfter, categoryFormatFunction)
        ];
    },

    /**
     * Create balance graph
     */
    _createBalanceDataGraph: function(valueField, color, balanceLabel, balanceFormatFunction, date, categoryFormatFunction)
    {
        return {
            "showBalloon": true,
            "fillAlphas": 1,
            "lineAlpha": 0,
            "type": "column",
            "valueField": valueField,
            "fillColors": color,
            "lineThickness": 0,
            "balloon": {
                "adjustBorderColor": true,
                "borderColor": "#FFFFFF",
                "borderThickness": 1,
                "fillAlpha": 0.85
            },
            "balloonFunction": this._createBalloonFunction(valueField, color, balanceLabel, balanceFormatFunction, date, categoryFormatFunction)
        }
    },

    _createBalloonFunction: function(valueField, iconColor, balanceLabel, balanceFormatFunction, date, categoryFormatFunction)
    {
        var balloonTemplate = _.template(
                "<div class='balanceChartBalloon'>" +
                "   <div class='category'><%-category%></div>" +
                "   <div class='icon' style='background-color: <%=iconColor%>'></div><%=balanceTypeText%>" +
                "   <div class='dateAndBalanceValue'><%=date%>:&nbsp;<span class='balanceValue <%=additionalBalanceClasses%>'><%=value%></span></div>" +
                "</div>"
        );

        return function(item)
        {
            var balance = item.dataContext[valueField];
            if (+balance == 0)
            {
                return null;
            }
            return balloonTemplate({
                                       "iconColor": iconColor,
                                       "value": balanceFormatFunction(balance),
                                       "balanceTypeText": balanceLabel,
                                       "date": date,
                                       "category": categoryFormatFunction(item.dataContext.category),
                                       "additionalBalanceClasses": balance < 0 ? "negative" : ""
                                   });
        };
    },

    _getAdditionalEventListeners: function()
    {
        var self = this;
        return [{
            "event": "zoomed",
            "method": function(event)
            {
                self._startIndex = event.startIndex;
                self._endIndex = event.endIndex;
            }
        }];
    },

    getExportChartConfig: function()
    {
        return {
            "startIndex": this._startIndex,
            "endIndex": this._endIndex,
            "maxMinScaleValues": this._maxMinScaleValues,
            "showScrollbar": false
        }
    },

    _updateSettingsForExport: function(settings, chartData, config)
    {
        var scales = this._getChartScales(config);
        settings["marginLeft"] *= scales.horizontal;
        settings["fontSize"] = settings["fontSize"] * scales.common;
        settings["categoryAxis"]["fontSize"] *= scales.common;
        settings["categoryAxis"]["minHorizontalGap"] *= scales.common;
        settings["categoryAxis"]["minVerticalGap"] *= scales.common;
        settings["marginBottom"] *= scales.vertical;
    }
});

export default BalancesChartGenerator;



// WEBPACK FOOTER //
// ./web/actiplans/js/reports/leaveTimeBalancesReportPage/chart/BalancesChartGenerator.js